import CryptoJS from 'crypto-js';
import {parsePhoneNumber} from 'react-phone-number-input/input';
import {BTTrackerBase} from './base';

export class TikTokTracker extends BTTrackerBase {
  constructor(args) {
    super(args);
    this.actions = ['identity', 'lead'];
  }

  configure({tiktokTag, ...restConfig}, ...restArgs) {
    super.configure({tiktokTag, ...restConfig}, ...restArgs);
    this.tiktokTag = tiktokTag;

    if (!tiktokTag) {
      this.disable();
      this.log('TikTok has been disabled because no tag was specified.');
    }
  }

  getName() {
    return 'TikTokTracker';
  }

  get requiresTrackingConsent() {
    return true;
  }

  get restrictsEventsWithoutConsent() {
    return true;
  }

  init(...params) {
    this.loadTag();
    this.log('TikTok initialized', {tiktokTag: this.tiktokTag});
    super.init(...params);
  }

  loadTag() {
    /* eslint-disable */
    !function (w, d, t,tg) {
      w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for(var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var i='https://analytics.tiktok.com/i18n/pixel/events.js';ttq._i=ttq._i||{},ttq._i[e]=[],ttq._i[e]._u=i,ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};var o=document.createElement("script");o.type="text/javascript",o.async=!0,o.src=i+"?sdkid="+e+"&lib="+t;var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(o,a)};

      ttq.load(tg);
    }(window, document, 'ttq',this.tiktokTag);
    /* eslint-enable */
  }

  view({productImpression, productDefaultVariant}, ...params) {
    if (!super.view({productImpression, productDefaultVariant}, ...params)) {
      return false;
    }

    window.ttq.page();
    this.log('Page view event fired.');

    if (productImpression === undefined || Object.keys(productImpression).length <= 0) {
      this.log('View content event will not be fired.  No information provided.');
      return false;
    }

    const variant = productImpression.variants.find(v => v.id === productDefaultVariant) || {};

    /* eslint-disable camelcase */
    const payload = {
      content_type: 'product',
      currency: 'USD',
      value: variant.price / 100,
      description: variant.name,
      contents: [{
        price: variant.price / 100,
        quantity: 1,
        content_id: variant.sku,
        content_name: variant.name,
        brand: productImpression.vendor,
      }],
    };
    /* eslint-enable camelcase */

    window.ttq.track('ViewContent', payload);
    this.log('View content event fired.', {payload});
    return true;
  }

  action(type, action, category, data) {
    if (!super.action(type, action, category, data)) {
      return false;
    }

    if (data?.success && this.actions.includes(type)) {
      const externalId = (action === 'shopify' && data?.customerId) ? data.customerId : '';
      const payload = this.getIdentityPayload(externalId, data?.email, data?.phone);

      /* eslint-disable camelcase */
      if (!payload.external_id && !payload.email && !payload.phone_number) {
        this.log('Tiktok identify event will not be fired. No information provided.', {payload});
        return false;
      }
      /* eslint-enable camelcase */

      window.ttq.identify(payload);
      this.log('Identify event fired', payload);
    }

    return true;
  }

  purchase(order, items, {addon} = {}) {
    if (!super.purchase(order, items, {addon})) {
      return false;
    }

    if (addon) {
      this.log('TikTok conversion tracker will not fire for an add-on to an existing order', {order, addon});
      return false;
    }

    if (!order || !items) {
      this.log('TikTok conversion tracker will not fire because the order/item is missing', order);
      return false;
    }

    /* eslint-disable camelcase */
    const tkItems = items.map(({sku, title, price, quantity}) => ({
      content_id: sku,
      content_name: title,
      content_type: 'product',
      quantity,
      price,
    }));

    const payload = {
      contents: tkItems,
      content_type: 'product',
      value: order.subtotal + order.shipping,
      currency: order.currency,
    };
    /* eslint-enable camelcase */

    const identityPayload = this.getIdentityPayload(
      order.shopifyCustomerId,
      order.email,
      order?.billingAddress?.phone || order?.shippingAddress?.phone,
    );

    if (identityPayload.external_id || identityPayload.email || identityPayload.phone_number) {
      window.ttq.identify(identityPayload);
      this.log('Identify event fired', identityPayload);
    } else {
      this.log('Tiktok identify event will not be fired. No information provided.', {identityPayload});
    }

    window.ttq.track('CompletePayment', payload);
    this.log('Tiktok CompletePayment event fired', {payload});
    return true;
  }

  getIdentityPayload(externalId, email, phone) {
    let phoneNumber = null;
    if (phone) {
      phoneNumber = parsePhoneNumber(phone);

      // If the phone number is not valid, try to parse it as a US number
      if (!phoneNumber?.isValid()) {
        phoneNumber = parsePhoneNumber(phone, 'US');
      }
    }

    /* eslint-disable camelcase */
    return {
      external_id: externalId ? CryptoJS.SHA256(String(externalId).trim()).toString() : '',
      email: email ? CryptoJS.SHA256(String(email).toLowerCase()).toString() : '',
      phone_number: phoneNumber?.isValid() ? CryptoJS.SHA256(phoneNumber.number).toString() : '',
    };
    /* eslint-enable camelcase */
  }
}
